import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { resolverPrecioProducto, cantidadFacturable } from "@/lib/precioProducto";


export const useProductStore = create()(
  persist(
    (set) => ({
      products: [],
      productsByMachine: [],
      productsByCompany: [],
      allQuantity: 0,
      total: 0,
      storeTopay: null,
      buyOrder: null,
      payMethod: null,
      payStatusOneClick: false,
      discount: 0,
      descuento_porcentaje: 0,
      descuento_2x1: 0,
      descuento_id: null, //cupon aplicado
      descuento_data: null,
      toPay: true,
      filterProductsByCompany: (empresa_id) => {
        set((state) => {
          return {
            productsByCompany: state.products.filter((item) => item.empresa_id == empresa_id)
          };
        });
      },
      addToCart: (newProduct, machineSelected) => {
        set((state) => {
          const existingProductIndex = state.products.findIndex(
            (item) => item.producto_id === newProduct.producto_id && item.maquina_id === newProduct.maquina_id
          );

          // Validar stock: no permitir agregar más unidades de las disponibles
          const stockDisponible = newProduct.stock ?? Infinity;
          if (newProduct.producto_cant > stockDisponible) {
            return state; // Sin cambios — la UI debe mostrar el mensaje al usuario
          }

          let updatedProducts;

          if (existingProductIndex === -1) {
            // Producto no encontrado, lo añade (creando un NUEVO array para el estado)
            updatedProducts = [...state.products, newProduct];
          } else {
            // Alineado con CartContext (Sima-appmovil ADD_ITEM): solo actualizar producto_cant.
            // NO reemplazar los campos de descuento para evitar pérdida de datos.
            updatedProducts = state.products.map((product, index) => {
              if (index === existingProductIndex) {
                return { ...product, producto_cant: newProduct.producto_cant };
              }
              return product;
            });
          }
          // Devolvemos el NUEVO array de productos, que React detectará como un cambio de estado
          return {
            products: updatedProducts,
            productsByMachine: updatedProducts.filter((item) => item.maquina_id == machineSelected)
          };
        });
      },
      addItem: (newProduct, maquina_id) => { // Agregamos 'maquina_id' como un nuevo parámetro
        set((state) => {
          // Busca el índice del producto existente en el carrito de la máquina específica
          const existingProductIndex = state.products.findIndex(
            (item) => item.producto_id === newProduct.producto_id && item.maquina_id === maquina_id
          );

          let updatedProducts;

          if (existingProductIndex === -1) {
            updatedProducts = [...state.products, newProduct]; // Agrega el nuevo producto directamente
          } else {
            // Si el producto ya existe en el carrito de esta máquina, actualiza su cantidad
            updatedProducts = state.products.map((product, index) => {
              if (index === existingProductIndex) {
                return { ...product, producto_cant: newProduct.producto_cant };
              }
              return product; // Retorna los demás productos sin cambios
            });
          }
          return { products: updatedProducts }; // Retorna el nuevo estado con el array actualizado
        });
      },
      clearCart: () => {
        set(() => {
          return {
            products: [],
            productsByMachine: [],
            productsByCompany: [],
            allQuantity: 0,
            total: 0,
            storeTopay: null,
            buyOrder: null,
            payMethod: null,
            payStatusOneClick: false,
            discount: 0,
            descuento_id: null,
            descuento_data: null,
            toPay: true,
          };
        });
      },
      removeFromCart: (id, maquina_id) => {
        set((state) => {
          // Filtra los productos para mantener solo aquellos que NO coinciden
          // con el 'id' O NO coinciden con la 'maquina_id'
          state.products = state.products.filter(
            (product) => !(product.producto_id === id && product.maquina_id === maquina_id)
          );
          return {
            products: state.products,
            productsByMachine: state.products.filter(
              (item) => item.maquina_id == maquina_id && Number(item.producto_cant) > 0
            )
          };
        });
      },
      clearCartByMachine: (maquina_id) => {
        set((state) => {
          const remainingProducts = state.products.filter((item) => item.maquina_id !== maquina_id);
          const allQuantity = remainingProducts.reduce(
            (sum, item) => (Number(item.producto_cant) > 0 ? sum + Number(item.producto_cant) : sum),
            0
          );
          return {
            products: remainingProducts,
            productsByMachine: [],
            allQuantity,
          };
        });
      },
      setStoreToPay: (id) => {
        set(() => {
          return { storeTopay: id };
        });
      },
      calculateTotals: (machineSelected) => {
        set((state) => {
          let amount = 0;
          let total = 0;

          const findProductsByStore = state.products.filter(
            (item) => item.maquina_id === machineSelected && Number(item.producto_cant) > 0
          );

          findProductsByStore.forEach((item) => {
            amount += item.producto_cant;

            // Precio unitario y unidades cobradas se resuelven en
            // src/lib/precioProducto.js — módulo puro que es la ÚNICA fuente
            // de verdad compartida con lo que muestran las cards de producto
            // y los subtotales del carrito. Antes este bloque repetía el
            // ladder completo y las copias ya habían divergido (guardas de
            // datos inválidos presentes en el módulo y ausentes acá), con el
            // resultado de mostrar un precio y cobrar otro. Ver ADR-0003.
            const { precioFinal } = resolverPrecioProducto(item);
            total += precioFinal * cantidadFacturable(item, item.producto_cant);
          });

          // total es siempre el subtotal bruto (sin descuento de cupón)
          // El descuento se aplica solo en la UI para evitar que se acumule
          return {
            products: state.products,
            productsByMachine: state.products.filter(
              (item) => item.maquina_id == machineSelected && Number(item.producto_cant) > 0
            ),
            allQuantity: amount,
            total: Math.round(total),
          };
        });
      },
      setBuyOrder: (order) => {
        set(() => {
          return { buyOrder: order };
        });
      },
      setFinishOrder: (machineSelected) => {
        set((state) => {
          return {
            products: state.products.filter((item) => item.maquina_id !== machineSelected),
            buyOrder: null,
            allQuantity: 0,
            total: 0,
            payMethod: null,
            payStatusOneClick: false,
            discount: 0,
            toPay: true,
          };
        });

      },
      setPayMethod: (payment) => {
        set(() => {
          return {
            payMethod: payment,
          };
        });

      },
      setPayStatus: (status) => {
        set(() => {
          return {
            payStatusOneClick: status,
          };
        });

      },
      setDiscount: (value, id, data) => {
        set(() => {
          return {
            discount: value,
            descuento_id: id,
            descuento_data: data,
          };
        });
      },
      setToPay: (status) => {
        set(() => {
          return {
            toPay: status,
          };
        });
      },
    }),
    {
      name: "products-storage", // name of the item in the storage (must be unique)
      storage: createJSONStorage(() => localStorage), // 'localStorage','sessionStorage' is used 
    }
  )
);